import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Text,
  Divider,
  FormControl,
  FormErrorMessage,
  HStack,
  Heading,
  Image,
  Input,
  Select as ChakraSelect,
  VStack,
  IconButton,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Select from 'react-select';
import countryCodes, { CountryProperty } from 'country-codes-list';
import { Controller } from 'react-hook-form';
import { useAbility } from '@casl/react';

import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import UploadIcon from '../../../assets/icons/upload.svg';
import { UserForm } from '../models';
import { CenteredSpinner } from '../../../components';
import { calculateFileSize } from '../../../utils';
import {
  useGetTeamMemberByIdQuery,
  useGetAllRolesQuery,
  useUpdateTeamMemberMutation,
  useCreateTeamMemberMutation,
  useGetPartnersQuery,
  useCreateNewPartnerMutation,
  displayNotification,
} from '../../../services';
import { useTypedSelector } from '../../../hooks';
import { Gender, Partner, Role, Status, TeamMember } from '../../../models';
import { AbilityContext } from '../../../providers';
import { CUIAutoComplete, Item } from 'chakra-ui-autocomplete';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const SavedUser: FC = () => {
  const [teamMemberStatus, setTeamMemberStatus] = useState<Status>();
  const [profilePicture, setProfilePicture] = useState({ name: '', size: 0 });
  const [selectedCompany, setSelectedCompany] = useState<
    { value: string; label: string } | undefined
  >();
  const ability = useAbility(AbilityContext);

  const params = useParams();
  const { userId } = params;
  const status = params.status || 'all';

  const { userDetails } = useTypedSelector(state => state.auth);

  const {
    data: partnersData,
    refetch: refetchPartners,
    isLoading: isPartnersLoading,
  } = useGetPartnersQuery({});

  const { data: teamMember, isLoading } = useGetTeamMemberByIdQuery(
    Number(userId),
    {
      skip: !userId,
    }
  );

  const { data: allRoles } = useGetAllRolesQuery(
    {
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
      page: 0,
      size: 10000,
    },
    {
      skip: !userDetails,
    }
  );

  const navigate = useNavigate();

  const [updateTeamMember] = useUpdateTeamMemberMutation();
  const [createTeamMember] = useCreateTeamMemberMutation();

  const options = countryCodes.all().map(country => ({
    value: country.countryCallingCode,
  }));

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    resetField,
    control,
    reset,
  } = useForm<UserForm>();

  const navigateToPreviousPage = () => navigate(`/users/${status}`);

  const [
    createPartner,
    { isLoading: isCreatePartnerLoading, status: createPartnerStatus },
  ] = useCreateNewPartnerMutation();

  const createNewPartner = async (data: { label: string; value: string }) => {
    const response = await createPartner({ name: data.value });

    if (!isMutationSuccess(response)) {
      notifyMutationFailure(response, {
        title: 'Could not create the company',
        description: 'The company was not created. Please try again.',
      });
    }
  };

  async function onSubmit(data: UserForm) {
    if (!selectedCompany?.value) {
      displayNotification({
        title: 'Please select a company!',
        description: 'User should have a defined company.',
        status: 'error',
      });
      return;
    }

    const {
      email,
      firstName,
      gender,
      lastName,
      phoneNumber,
      profilePicture,
      role,
      countryCode,
    } = data;
    const formData = new FormData();
    if (profilePicture && profilePicture[0])
      formData.append('profilePicture', profilePicture[0]);

    const teamMemberInfo: Partial<TeamMember> = {
      email,
      firstName,
      gender,
      lastName,
      phoneNumber,
      role: { id: role } as Role,
      countryCode: countryCode.value,
      companyBasicInfo: userDetails?.companyBasicInfo,
      partner: { id: selectedCompany.value },
    };

    if (userId) {
      teamMemberInfo.id = Number(userId);
      teamMemberInfo.status = teamMemberStatus;
    }

    formData.append('teamMember', JSON.stringify(teamMemberInfo));
    try {
      // Neither trigger is unwrapped, so a rejected save resolved normally and
      // the page navigated away as if it had been saved, discarding the form.
      const response = userId
        ? await updateTeamMember(formData)
        : await createTeamMember(formData);

      if (!isMutationSuccess(response)) {
        notifyMutationFailure(response, {
          title: userId
            ? 'Could not update this team member'
            : 'Could not create the team member',
          description:
            'Your changes were not saved. Please review the form and try again.',
        });
        return;
      }

      navigateToPreviousPage();
    } catch (error) {
      console.error(error);
      displayNotification({
        title: 'Something went wrong!',
        description:
          'Your changes were not saved. Please try this action later or contact BrickLynk support.',
        status: 'error',
      });
    }
  }

  useEffect(() => {
    if (createPartnerStatus === QueryStatus.fulfilled) {
      refetchPartners();
    }
  }, [createPartnerStatus]);

  useEffect(() => {
    if (teamMember?.data) {
      const {
        countryCode,
        email,
        firstName,
        lastName,
        phoneNumber,
        status,
        profilePictureFileName,
        profilePictureSize,
        gender,
        role,
        partner,
      } = teamMember.data;

      if (profilePictureFileName) {
        setProfilePicture({
          name: profilePictureFileName.split('/').at(-1),
          size: Number(profilePictureSize),
        });
      }

      reset({
        countryCode: { value: countryCode },
        email,
        firstName,
        lastName,
        phoneNumber,
        gender,
        role: role.id,
      });

      if (partner?.name) {
        setSelectedCompany({
          label: partner?.name,
          value: partner?.id,
        });
      }

      setTeamMemberStatus(status);
    }
  }, [teamMember?.data, isLoading, reset]);

  if (isLoading || isPartnersLoading) return <CenteredSpinner />;

  return (
    <Box>
      <HStack justify="space-between" mt="4" align="start">
        <Heading color="custom_blue.1" fontSize="2xl">
          {userId ? 'Update Team Member' : 'Add a New Team Member'}
        </Heading>
      </HStack>

      <VStack
        as="form"
        align="stretch"
        gap="5"
        onSubmit={handleSubmit(onSubmit)}
        bg="white"
        border="1px"
        borderColor="custom_gray.3"
        mt="7"
        mb="4"
        p="5"
        borderRadius="md"
        minH="80vh"
        h="auto"
      >
        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              First Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.firstName} flexBasis="50%" mt="2">
            <Input
              placeholder="First Name"
              {...register('firstName', {
                required: 'First Name is required',
              })}
            />
            <FormErrorMessage>
              {errors.firstName && errors.firstName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Last Name
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.lastName} flexBasis="50%" mt="2">
            <Input
              placeholder="Last Name"
              {...register('lastName', {
                required: 'Last Name is required',
              })}
            />
            <FormErrorMessage>
              {errors.lastName && errors.lastName.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Profile Picture
            </Box>
          </VStack>

          <Box flexBasis="50%">
            <FormControl isInvalid={true}>
              <Input
                display="none"
                id="profilePicture"
                onInput={e => {
                  const files = (e.target as HTMLInputElement).files;
                  if (!files) return;
                  const [file] = files;
                  const { name, size } = file;

                  setProfilePicture({ name, size });
                }}
                type="file"
                accept=".png, .jpg"
                {...register('profilePicture', {
                  required: userId ? false : 'Profile Picture is required',
                })}
              />

              <label htmlFor="profilePicture">
                <VStack
                  cursor="pointer"
                  border="2px"
                  borderRadius="md"
                  borderStyle="dashed"
                  borderColor="custom_orange.2"
                  py="3"
                >
                  <Box
                    border="1px"
                    borderColor="custom_gray.4"
                    borderRadius="md"
                    p="2"
                  >
                    <Image src={UploadIcon} />
                  </Box>

                  <Text color="custom_blue.1" fontSize="sm">
                    <Box
                      as="span"
                      color="custom_orange.2"
                      fontWeight="semibold"
                    >
                      Click to upload
                    </Box>{' '}
                    or drag and drop
                  </Text>
                  <Text color="custom_blue.1" fontSize="xs">
                    PNG, JPG
                  </Text>
                </VStack>
              </label>

              {profilePicture.name && (
                <VStack
                  align="start"
                  border="1px"
                  borderRadius="md"
                  borderColor="custom_gray.4"
                  p="3"
                  mt="3"
                >
                  <HStack justify="space-between" w="full">
                    <Box
                      fontWeight="medium"
                      color="custom_blue.1"
                      fontSize="sm"
                    >
                      {profilePicture.name}
                    </Box>

                    <IconButton
                      icon={<TrashIcon />}
                      aria-label="delete uploaded company logo"
                      bg="transparent"
                      onClick={() => {
                        setProfilePicture({ name: '', size: 0 });
                        resetField('profilePicture', {
                          keepDirty: true,
                          keepError: true,
                          keepTouched: true,
                        });
                      }}
                    />
                  </HStack>
                  <Box color="custom_blue.1" fontSize="sm">
                    {calculateFileSize(profilePicture.size)} MB
                  </Box>
                </VStack>
              )}
              <FormErrorMessage>
                {errors.profilePicture && errors.profilePicture.message}
              </FormErrorMessage>
            </FormControl>
          </Box>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Gender
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.gender} flexBasis="50%" mt="2">
            <ChakraSelect
              {...register('gender', {
                required: 'Gender is required',
              })}
            >
              <option />
              {[
                { value: Gender.MALE, text: 'Male' },
                { value: Gender.FEMALE, text: 'Female' },
              ].map(({ text, value }) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </ChakraSelect>
            <FormErrorMessage>
              {errors.gender && errors.gender.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Phone Number
            </Box>
          </VStack>
          <FormControl
            isInvalid={!!errors.phoneNumber || !!errors.countryCode}
            flexBasis="50%"
          >
            <HStack>
              <Box flexBasis="40%">
                <Controller
                  control={control}
                  name="countryCode"
                  rules={{
                    required: 'Country code is required',
                  }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={options}
                      getOptionLabel={({ value }) => {
                        const country = countryCodes.findOne(
                          'countryCallingCode' as CountryProperty,
                          value
                        );

                        return (
                          <div className="flex items-center">
                            <span
                              className={`fi fi-${country.countryCode.toLowerCase()} fis`}
                            ></span>
                            <span
                              style={{
                                marginLeft: 5,
                                width: '80px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {value}
                            </span>
                          </div>
                        );
                      }}
                    />
                  )}
                />
              </Box>

              <Input
                placeholder="Phone number"
                {...register('phoneNumber', {
                  required: 'Phone number is required',
                })}
              />
            </HStack>
            <FormErrorMessage>
              {(errors.phoneNumber && errors.phoneNumber.message) ||
                (errors.countryCode && errors.countryCode.message)}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Email
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.email} flexBasis="50%">
            <Input
              placeholder="Email"
              {...register('email', {
                required: 'Email is required',
              })}
            />
            <FormErrorMessage>
              {errors.email && errors.email.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Company
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.role} flexBasis="50%">
            <ChakraSelect
              value={selectedCompany?.value}
              {...register('partner', {
                required: 'Company is required',
              })}
              onChange={e => {
                setSelectedCompany({
                  label: e.target.value,
                  value: e.target.value,
                });
              }}
            >
              <option />
              {partnersData?.data &&
                partnersData?.data?.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
            </ChakraSelect>

            {/* <CUIAutoComplete
              
              placeholder="Select/Enter a company name"
              onCreateItem={createNewPartner}
              selectedItems={
                (selectedCompany as Item) ? [selectedCompany as Item] : []
              }
              onSelectedItemsChange={changes => {
                if (changes.selectedItems) {
                  setSelectedCompany(
                    changes?.selectedItems[changes?.selectedItems.length - 1]
                  );
                }
              }}
            /> */}
          </FormControl>
        </HStack>
        <Divider />

        <HStack gap="4" align="flex-start">
          <VStack color="custom_blue.1" align="start" flexBasis="25%">
            <Box fontSize="sm" fontWeight="semibold">
              Role
            </Box>
          </VStack>
          <FormControl isInvalid={!!errors.role} flexBasis="50%">
            <ChakraSelect
              {...register('role', {
                required: 'Role is required',
                disabled:
                  ability.cannot('POST', 'company/teamMember/reassignRole') &&
                  Boolean(userId),
              })}
            >
              <option />
              {allRoles?.data.content.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </ChakraSelect>
            <FormErrorMessage>
              {errors.role && errors.role.message}
            </FormErrorMessage>
          </FormControl>
        </HStack>

        <HStack mt={4} justify="end">
          <Button onClick={navigateToPreviousPage}>Cancel</Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            bg="custom_orange.2"
            _hover={{ bg: 'custom_orange.2' }}
            color="white"
          >
            Save
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};
